import type { DayHours } from '@/types'

import {
    OPENING_HOURS,
    SLOT_INTERVAL_MINUTES,
    LAST_SLOT_BEFORE_CLOSE_MINUTES,
    SAME_DAY_NOTICE_MINUTES,
    BOOKING_DAYS_AHEAD
} from '@/constants/business'

export type DayOption = {
    iso: string
    label: string
    dayNumber: string
    month: string
    available: boolean
    closed: boolean
}

export type OpenStatus = {
    isOpen: boolean
    label: string
}

export type HoursGroup = {
    days: string
    hours: string
}

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/

const pad = (value: number): string => String(value).padStart(2, '0')

const formatUtcIso = (date: Date): string => `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())}`

const parseIsoDate = (isoDate: string): Date | null => {
    const match = ISO_DATE.exec(isoDate)

    if (!match) return null

    const year = Number(match[1])
    const month = Number(match[2])
    const day = Number(match[3])
    const date = new Date(Date.UTC(year, month - 1, day))

    if (date.getUTCFullYear() !== year || date.getUTCMonth() !== month - 1 || date.getUTCDate() !== day) return null

    return date
}

const dateFormat = (options: Intl.DateTimeFormatOptions): Intl.DateTimeFormat =>
    new Intl.DateTimeFormat('en-GB', { ...options, timeZone: 'UTC' })

export const toMinutes = (time: string): number => {
    const [hours, minutes] = time.split(':').map(Number)

    return hours * 60 + minutes
}

export const formatClock = (totalMinutes: number): string => `${pad(Math.floor(totalMinutes / 60))}:${pad(totalMinutes % 60)}`

export const toIsoDate = (date: Date): string => `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`

export const todayUtcIso = (): string => formatUtcIso(new Date())

export const isValidIsoDate = (isoDate: string): boolean => parseIsoDate(isoDate) !== null

export const addDays = (isoDate: string, days: number): string => {
    const date = parseIsoDate(isoDate)

    if (!date) return isoDate

    date.setUTCDate(date.getUTCDate() + days)

    return formatUtcIso(date)
}

export const getWeekday = (isoDate: string): number | null => {
    const date = parseIsoDate(isoDate)

    return date ? date.getUTCDay() : null
}

export const getHoursForDay = (weekday: number): DayHours | undefined => OPENING_HOURS.find(entry => entry.day === weekday)

export const formatHoursRange = (entry: DayHours): string => entry.open && entry.close ? `${entry.open}–${entry.close}` : 'Closed'

export const getSlotsForDate = (isoDate: string): Array<string> => {
    const weekday = getWeekday(isoDate)

    if (weekday === null) return []

    const hours = getHoursForDay(weekday)

    if (!hours?.open || !hours.close) return []

    const slots: Array<string> = []
    const lastSlot = toMinutes(hours.close) - LAST_SLOT_BEFORE_CLOSE_MINUTES

    for (let time = toMinutes(hours.open); time <= lastSlot; time += SLOT_INTERVAL_MINUTES) {
        slots.push(formatClock(time))
    }

    return slots
}

export const isSlotTooSoon = (slot: string, nowMinutes: number): boolean => toMinutes(slot) < nowMinutes + SAME_DAY_NOTICE_MINUTES

export const formatLongDate = (isoDate: string): string => {
    const date = parseIsoDate(isoDate)

    return date ? dateFormat({ weekday: 'long', day: 'numeric', month: 'long' }).format(date) : isoDate
}

export const buildDayOptions = (startIso: string, nowMinutes: number, count: number = BOOKING_DAYS_AHEAD): Array<DayOption> => {
    const weekdayFormat = dateFormat({ weekday: 'short' })
    const monthFormat = dateFormat({ month: 'short' })
    const options: Array<DayOption> = []

    for (let index = 0; index < count; index++) {
        const iso = addDays(startIso, index)
        const date = parseIsoDate(iso)

        if (!date) continue

        const slots = getSlotsForDate(iso)
        const available = index === 0
            ? slots.some(slot => !isSlotTooSoon(slot, nowMinutes))
            : slots.length > 0

        options.push({
            iso,
            label: index === 0 ? 'Today' : index === 1 ? 'Tomorrow' : weekdayFormat.format(date),
            dayNumber: String(date.getUTCDate()),
            month: monthFormat.format(date),
            available,
            closed: slots.length === 0
        })
    }

    return options
}

export const getOpenStatus = (now: Date): OpenStatus => {
    const weekday = now.getDay()
    const minutes = now.getHours() * 60 + now.getMinutes()
    const today = getHoursForDay(weekday)

    if (today?.open && today.close) {
        if (minutes >= toMinutes(today.open) && minutes < toMinutes(today.close)) {
            return { isOpen: true, label: `Open now, closes ${today.close}` }
        }

        if (minutes < toMinutes(today.open)) {
            return { isOpen: false, label: `Closed, opens ${today.open}` }
        }
    }

    for (let offset = 1; offset <= 7; offset++) {
        const next = getHoursForDay((weekday + offset) % 7)

        if (next?.open) {
            const when = offset === 1 ? 'tomorrow' : next.name

            return { isOpen: false, label: `Closed, opens ${when} ${next.open}` }
        }
    }

    return { isOpen: false, label: 'Closed' }
}

export const groupOpeningHours = (): Array<HoursGroup> => {
    const groups: Array<{ first: DayHours, last: DayHours, hours: string }> = []

    for (const entry of OPENING_HOURS) {
        const hours = formatHoursRange(entry)
        const current = groups[groups.length - 1]

        if (current && current.hours === hours) current.last = entry
        else groups.push({ first: entry, last: entry, hours })
    }

    return groups.map(group => ({
        days: group.first === group.last ? group.first.short : `${group.first.short}–${group.last.short}`,
        hours: group.hours
    }))
}

export const countOpenDays = (): number => OPENING_HOURS.filter(entry => entry.open && entry.close).length
